import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, ShoppingBag, IndianRupee, ArrowUpRight, Zap, Download, Wallet, UserCheck, TrendingUp } from "lucide-react"; 
import { useAuth } from "../../context/AuthContext";

const AdminDashboard = () => {
  const [userStats, setUserStats] = useState([]);
  const [income, setIncome] = useState(0);
  const [ordersCount, setOrdersCount] = useState(0);
  const [usersCount, setUsersCount] = useState(0);
  const [walletTotal, setWalletTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fiscalYear, setFiscalYear] = useState("2024");
  
  const { user } = useAuth();
  const token = user?.accessToken || user?.token;
  const MONTHS = useMemo(() => ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"], []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!token) return;
      setIsLoading(true);
      const config = { headers: { token: `Bearer ${token}` } };

      try {
        const [uRes, iRes, oRes] = await Promise.allSettled([
          axios.get("https://wethrift-backend.onrender.com/api/users/stats", config),
          axios.get("https://wethrift-backend.onrender.com/api/orders/income", config),
          axios.get("https://wethrift-backend.onrender.com/api/orders", config)
        ]);

        if (uRes.status === "fulfilled") {
          const sortedData = uRes.value.data.sort((a, b) => a._id - b._id);
          const chartMap = sortedData.map(item => ({
            name: MONTHS[item._id - 1],
            "Users": item.total
          }));
          setUserStats(chartMap);
          setUsersCount(sortedData.reduce((acc, curr) => acc + curr.total, 0));
        }

        if (iRes.status === "fulfilled") {
          const total = iRes.value.data.reduce((acc, curr) => acc + (curr.total || 0), 0);
          setIncome(total);
        }

        if (oRes.status === "fulfilled") {
          setOrdersCount(oRes.value.data.length);
        }
      } catch (err) {
        console.error("Dashboard Sync Error:", err);
      } finally {
        setTimeout(() => setIsLoading(false), 500);
      }
    };

    fetchDashboardData();
  }, [MONTHS, token, fiscalYear]);

  const StatCard = ({ title, value, icon: Icon, color, trend }) => (
    <div className="group relative bg-zinc-900/40 border border-white/[0.05] p-6 rounded-[1.5rem] hover:bg-zinc-800/50 transition-all duration-300">
      <div className="flex justify-between items-start mb-4">
        <div className={`p-3 rounded-xl bg-black/40 border border-white/[0.05] ${color}`}>
          <Icon size={20} />
        </div>
        <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-400/5 px-2 py-1 rounded-lg">
          {trend} <ArrowUpRight size={10} />
        </div>
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase text-zinc-500 tracking-[0.15em] mb-1">{title}</p>
        <h3 className="text-2xl font-bold text-white tracking-tight">
          {isLoading ? <div className="h-7 w-20 bg-white/5 animate-pulse rounded" /> : value}
        </h3>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black p-4 sm:p-6 lg:p-10 font-sans text-white">
      
      {/* --- HEADER --- */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Market <span className="text-zinc-500 font-medium">Overview</span></h1>
          <p className="text-zinc-500 text-sm">Real-time business performance for the last 12 months.</p>
        </div>

        <div className="flex items-center gap-3">
          <select 
            value={fiscalYear}
            onChange={(e) => setFiscalYear(e.target.value)}
            className="bg-zinc-900 border border-white/10 text-xs font-bold px-4 py-2.5 rounded-xl outline-none cursor-pointer"
          >
            <option value="2024">FY 2024</option>
            <option value="2023">FY 2023</option>
          </select>
          <button className="bg-white text-black px-5 py-2.5 rounded-xl text-xs font-bold uppercase hover:scale-95 transition-all flex items-center gap-2">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* --- STATS GRID --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard 
          title="Revenue" 
          value={`₹${income.toLocaleString('en-IN')}`} 
          icon={IndianRupee} 
          color="text-emerald-400" 
          trend="+12%" 
        />
        <StatCard 
          title="Total Users" 
          value={usersCount} 
          icon={Users} 
          color="text-blue-400" 
          trend="+5%" 
        />
        <StatCard 
          title="Orders" 
          value={ordersCount} 
          icon={ShoppingBag} 
          color="text-orange-400" 
          trend="+18%" 
        />
        <StatCard 
          title="Wallet Assets" 
          value={`₹${walletTotal.toLocaleString('en-IN')}`} 
          icon={Wallet} 
          color="text-purple-400" 
          trend="+2%" 
        />
      </div>

      {/* --- MAIN CHART --- */}
      <div className="bg-zinc-900/20 border border-white/[0.05] p-6 lg:p-8 rounded-[2rem] mb-8">
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-8">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <TrendingUp size={18} className="text-blue-500" />
              Growth Analytics
            </h2>
            <p className="text-zinc-500 text-xs mt-1">Monthly registration velocity</p>
          </div>
          <div className="flex gap-4">
             <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-[10px] font-bold text-zinc-400 uppercase">New Users</span>
             </div>
          </div>
        </div>

        <div style={{ width: '100%', height: 300 }}> 
  <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={userStats}>
              <defs>
                <linearGradient id="chartColor" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff05" vertical={false} />
              <XAxis 
                dataKey="name" 
                axisLine={false} 
                tickLine={false} 
                tick={{fill: '#52525b', fontSize: 10, fontWeight: 600}}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{fill: '#52525b', fontSize: 10, fontWeight: 600}}
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#18181b', border: 'none', borderRadius: '12px', fontSize: '12px' }}
                itemStyle={{ color: '#fff' }}
              />
              <Area 
                type="monotone" 
                dataKey="Users" 
                stroke="#3b82f6" 
                strokeWidth={3}
                fillOpacity={1} 
                fill="url(#chartColor)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* --- FOOTER INFO --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-zinc-900/30 border border-white/[0.05] p-6 rounded-2xl flex items-center gap-5">
          <div className="bg-emerald-500/10 p-4 rounded-full">
            <UserCheck className="text-emerald-500" size={24} />
          </div>
          <div>
            <h4 className="text-sm font-bold">System Integrity</h4>
            <p className="text-xs text-zinc-500">All data nodes are synced and operational.</p>
          </div>
        </div>
        
        <div className="bg-zinc-900/30 border border-white/[0.05] p-6 rounded-2xl flex items-center gap-5">
           <div className="bg-blue-500/10 p-4 rounded-full">
            <Zap className="text-blue-500" size={24} />
          </div>
          <div>
            <h4 className="text-sm font-bold">Auto-Refresh Enabled</h4>
            <p className="text-xs text-zinc-500">Live updates fetching every 60 seconds.</p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminDashboard;